/**
 * The ban form: a duration from the shared list, and a reason the operator types. Both required.
 *
 * One component for both places a ban is issued — the Moderation tab's search, and the Ban
 * button on the Users tab — because the decision is the same one in both, and a second copy of
 * this form would be a second place to forget that the reason is shown to the player.
 *
 * The duration picker is `BAN_DURATIONS` from `@chompdome/shared` rather than a list restated
 * here; the server validates against the same array, so a duration this dialog can offer is a
 * duration the API accepts. The reason is bounded by the shared lengths for the same reason.
 */

import {
  BAN_DURATIONS,
  BAN_REASON_MAX_LENGTH,
  BAN_REASON_MIN_LENGTH,
  type BanDurationId,
} from '@chompdome/shared/constants';
import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Dialog } from '@/components/ui/dialog';

/** The account being banned, as either caller knows it. */
export interface BanTarget {
  id: string;
  displayName: string;
  email: string | null;
  isGuest: boolean;
  role: string;
}

export interface BanDialogProps {
  target: BanTarget;
  /** The server's refusal, shown verbatim — it is the sentence that explains the staff rules. */
  serverError: string | null;
  pending: boolean;
  onSubmit: (input: { duration: BanDurationId; reason: string }) => void;
  onClose: () => void;
}

export function BanDialog({ target, serverError, pending, onSubmit, onClose }: BanDialogProps) {
  const [duration, setDuration] = useState<BanDurationId>(BAN_DURATIONS[0].id);
  const [reason, setReason] = useState('');

  const trimmed = reason.trim();
  const tooShort = trimmed.length < BAN_REASON_MIN_LENGTH;

  return (
    <Dialog
      open
      title={`Ban ${target.displayName}`}
      description={target.email ?? 'Guest account'}
      onClose={onClose}
    >
      {target.isGuest && (
        <p className="mb-3 rounded-lg border border-border bg-muted/40 p-3 text-xs text-muted-foreground">
          This is a guest. A guest row is reclaimed by a device id anybody can send, so banning one
          is a speed bump, not an identity: it ends the match they are in and stops this account
          being turned into a registered one, and they can start again as a new guest in seconds.
          Ban registered accounts.
        </p>
      )}

      <form
        className="space-y-3"
        onSubmit={(event) => {
          event.preventDefault();
          if (tooShort) return;
          onSubmit({ duration, reason: trimmed });
        }}
      >
        <label className="block text-sm">
          <span className="mb-1 block font-medium">How long</span>
          <select
            className="h-9 w-full rounded-lg border border-input bg-background px-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50"
            aria-label="Ban duration"
            value={duration}
            disabled={pending}
            onChange={(event) => setDuration(event.target.value as BanDurationId)}
          >
            {BAN_DURATIONS.map((entry) => (
              <option key={entry.id} value={entry.id}>
                {entry.label}
              </option>
            ))}
          </select>
        </label>

        <label className="block text-sm">
          <span className="mb-1 block font-medium">Reason</span>
          <textarea
            className="min-h-20 w-full rounded-lg border border-input bg-background p-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50"
            aria-label="Ban reason"
            maxLength={BAN_REASON_MAX_LENGTH}
            value={reason}
            disabled={pending}
            onChange={(event) => setReason(event.target.value)}
          />
          <span className="mt-1 block text-xs text-muted-foreground">
            The player is shown this, so write it for them. {trimmed.length}/{BAN_REASON_MAX_LENGTH}
          </span>
        </label>

        {serverError && (
          <p className="text-sm text-destructive" role="alert">
            {serverError}
          </p>
        )}

        <div className="flex justify-end gap-2">
          <Button type="button" variant="outline" size="sm" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" variant="destructive" size="sm" disabled={pending || tooShort}>
            {pending ? 'Banning…' : 'Ban account'}
          </Button>
        </div>
      </form>
    </Dialog>
  );
}
